import React, {
    forwardRef,
    FunctionComponent,
    HTMLProps,
    SyntheticEvent,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import classnames from 'classnames';
import { cloneDeep, isEqual, kebabCase, truncate } from 'lodash';
import { Button, ButtonGroup, Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import { MultiSelectFilter } from 'common/filters/filter-bar/multi-select-filter/MultiSelectFilter';
import { SingleSelectFilter } from 'common/filters/filter-bar/single-select-filter/SingleSelectFilter';
import { isMultiSelect } from 'common/filters/Filters';
import { FilterDefinition, FilterValue } from 'common/filters/Filters.types';
import { Overlay } from 'core/overlay/Overlay';

import 'common/filters/filter-bar/selected-filter/SelectedFilter.scss';

interface Props {
    className?: string;
    filter: FilterDefinition;
    onChange: (filter: FilterDefinition) => void;
    onRemove: (filter: FilterDefinition) => void;
    showSelectAll?: boolean;
    isFilterDisabled?: boolean;
}

export const SelectedFilter: FunctionComponent<Props> = ({
    className,
    filter,
    onChange,
    onRemove,
    showSelectAll,
    isFilterDisabled = false,
}) => {
    const { t } = useTranslation(['common/filters']);

    const [isOpen, setOpen] = useState(false);
    const [previousValues, setPreviousValues] = useState(filter.values.filter((filterValue) => filterValue.selected));
    const [multiSelectDraft, setMultiSelectDraft] = useState<FilterValue[] | null>(null);
    const [appliedValues, setAppliedValues] = useState<FilterValue[]>(cloneDeep(filter.values));

    const lastPropValuesRef = useRef<FilterValue[]>(cloneDeep(filter.values));

    useEffect(() => {
        if (isEqual(lastPropValuesRef.current, filter.values)) {
            return;
        }

        lastPropValuesRef.current = cloneDeep(filter.values);
        setAppliedValues(cloneDeep(filter.values));
        setPreviousValues(filter.values.filter((filterValue) => filterValue.selected));
    }, [filter.values]);

    useEffect(() => {
        if (!isOpen) return undefined;

        // small delay ensures dropdown is rendered
        const id = setTimeout(() => {
            containerRef.current?.querySelector<HTMLInputElement>('input')?.focus();
        }, 0);

        return () => clearTimeout(id);
    }, [isOpen]);

    const containerRef = useRef<HTMLDivElement | null>(null);
    const toggleRef = useRef<HTMLButtonElement | null>(null);
    const multiSelectDraftRef = useRef<FilterValue[] | null>(null);

    const effectiveFilter = useMemo(
        () => ({
            ...filter,
            values: appliedValues,
        }),
        [filter, appliedValues],
    );
    const handleDraftChange = (values: FilterValue[]): void => {
        multiSelectDraftRef.current = values;
        setMultiSelectDraft(values);
    };

    const openDropdown = (): void => {
        if (isMultiSelect(effectiveFilter)) {
            const initialDraft = cloneDeep(appliedValues);

            setMultiSelectDraft(initialDraft);
            multiSelectDraftRef.current = initialDraft;
        }

        setOpen(true);
    };

    const closeDropdown = (): void => {
        if (isMultiSelect(effectiveFilter)) {
            const sourceValues = multiSelectDraftRef.current ?? multiSelectDraft ?? appliedValues;
            const currentValues = sourceValues.filter((filterValue) => filterValue.selected);
            const newValues = currentValues.map((value) => value.value).sort();
            const oldValues = previousValues.map((value) => value.value).sort();

            if (!isEqual(newValues, oldValues)) {
                const nextValues = cloneDeep(sourceValues);

                setAppliedValues(nextValues);
                setPreviousValues(currentValues);
                lastPropValuesRef.current = nextValues;

                onChange({
                    ...filter,
                    values: nextValues,
                });
            }
        }

        setOpen(false);
    };

    const toggle = (open: boolean, event: SyntheticEvent<Dropdown>, metadata?: { source?: string }): void => {
        if (open) {
            openDropdown();
            return;
        }

        if (isMultiSelect(filter) && metadata?.source === 'select') {
            return;
        }
        closeDropdown();
    };

    const getFilterHeader = (): string => {
        const selectedValues = isOpen ? previousValues : appliedValues.filter((filterValue) => filterValue.selected);

        let header: string;

        if (isMultiSelect(effectiveFilter)) {
            const HEADER_MAX_LENGTH = 20;

            if (selectedValues.length === 0 || selectedValues.length === appliedValues.length) {
                header = t('ALL');
            } else {
                header = selectedValues.map((value) => value.label).join(', ');
                if (header.length > HEADER_MAX_LENGTH) {
                    header = `${truncate(header, { length: HEADER_MAX_LENGTH })} (${selectedValues.length})`;
                }
            }
        } else if (selectedValues.length) {
            header = selectedValues[0]!.dateRange ? selectedValues[0]!.dateRange!.label : selectedValues[0]!.label;
        } else {
            header = '';
        }

        return header;
    };

    const onSelect = (nextFilter: FilterDefinition): void => {
        const nextValues = cloneDeep(nextFilter.values);

        setAppliedValues(nextValues);
        setPreviousValues(nextValues.filter((value) => value.selected));
        lastPropValuesRef.current = nextValues;

        onChange({
            ...nextFilter,
            values: nextValues,
        });

        setOpen(false);
    };

    type ButtonProps = HTMLProps<HTMLButtonElement>;

    const SelectedFilterToggle = forwardRef<HTMLButtonElement, ButtonProps>((props, forwardedRef) => (
        <Button
            aria-haspopup="menu"
            aria-expanded={isOpen}
            aria-controls={`selected-filter-menu-${filter.name}`}
            ref={forwardedRef}
            className={props.className}
            variant="default"
            onClick={(e: any): void => {
                toggle(!isOpen, e);
            }}
            disabled={isFilterDisabled}
            onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) => {
                if (e.key !== 'ArrowDown') return;

                e.preventDefault();

                if (!isOpen) {
                    toggle(true, e as any);
                }

                requestAnimationFrame(() => {
                    const root = containerRef.current;

                    if (!root) return;

                    const menu = root.querySelector<HTMLElement>('[data-filter-menu="true"]');

                    if (!menu) return;

                    const focusable = menu.querySelector<HTMLElement>(
                        'input:not([disabled]), button:not([disabled]), [href], select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
                    );

                    focusable?.focus();
                });
            }}
        >
            {props.children}
        </Button>
    ));
    const toggleId = `selected-filter-toggle-${filter.name}`;

    return (
        <div
            className={classnames(`r4e-selected-filter ${kebabCase(filter.label)}`, { open: isOpen }, className)}
            ref={containerRef}
            onKeyDownCapture={(e) => {
                if (!isOpen) return;
                const target = e.target as HTMLElement;
                const isInsideCustomDateFilter = !!target.closest('.r4e-custom-date-filter');

                // Forward tab exits dropdown
                if (e.key === 'Tab' && !e.shiftKey) {
                    if (isInsideCustomDateFilter) return;
                    closeDropdown();
                    // IMPORTANT: don't preventDefault
                }
                // Esc closes + returns focus to toggle
                if (e.key === 'Escape') {
                    e.preventDefault();
                    closeDropdown();
                    requestAnimationFrame(() => {
                        toggleRef.current?.focus();
                    });
                }
            }}
        >
            <Overlay key={`selected-filter-${filter.name}`} area={`selected-filter-${filter.name}`} />
            <ButtonGroup className={classnames(`filter`, { 'not-locked': !filter.locked })}>
                {!filter.locked && (
                    <Button
                        aria-label="Clear Filter"
                        className={classnames('btn-close', { locked: filter.locked })}
                        disabled={filter.locked}
                        size="sm"
                        variant="default"
                        onClick={(): void => onRemove(filter)}
                    >
                        <i className="icon-times" />
                    </Button>
                )}
                <Dropdown as={ButtonGroup} show={isOpen} onToggle={toggle} autoClose={false}>
                    <Dropdown.Toggle
                        id={toggleId}
                        ref={toggleRef}
                        aria-label={`${filter.label}: ${getFilterHeader()}. ${isOpen ? 'Expanded' : 'Collapsed'}`}
                        as={SelectedFilterToggle}
                        className={classnames('filter-toggle', { closable: !filter.locked })}
                    >
                        <span className="filter-label">{filter.label}:</span>
                        <span className="filter-header">{getFilterHeader()}</span>
                    </Dropdown.Toggle>
                    {isMultiSelect(effectiveFilter) ? (
                        <Dropdown.Menu
                            data-filter-menu="true"
                            showSelectAll={showSelectAll}
                            filter={effectiveFilter}
                            show={isOpen}
                            onDraftChange={handleDraftChange}
                            showSearch
                            as={MultiSelectFilter}
                            aria-labelledby={toggleId}
                        >
                            <Overlay area="menu" />
                        </Dropdown.Menu>
                    ) : (
                        <Dropdown.Menu
                            data-filter-menu="true"
                            aria-labelledby={toggleId}
                            filter={effectiveFilter}
                            onChange={onSelect}
                            showSearch
                            isOpen={isOpen}
                            as={SingleSelectFilter}
                        >
                            <Overlay area="menu" />
                        </Dropdown.Menu>
                    )}
                </Dropdown>
            </ButtonGroup>
        </div>
    );
};
